/**
 * Customer Ops — Live Telemetry Dashboard.
 *
 * Channel-aware: every panel checks `channels.<name>` before rendering, so
 * tenants on PHARMA / DIAGNOSTIC / BULLION profiles only see what they pay for.
 * Polls /api/v1/ops/telemetry/dashboard every 5 s for the live feel.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Thermometer, Droplets, MapPin, PackageOpen, Scale, Landmark,
  Activity, RefreshCw, Wifi, AlertTriangle, ChevronRight, Route, Clock,
  FileSpreadsheet, Download, Database, Truck, Bell,
} from 'lucide-react';
import { opsApi } from '@/services/api';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, Area, AreaChart,
} from 'recharts';
import SetPasswordBanner from '@/components/SetPasswordBanner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Leaflet marker icon (live truck)
delete L.Icon.Default.prototype._getIconUrl;
const liveIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const PROFILE_LABELS = {
  PHARMA: 'Pharmaceutical',
  DIAGNOSTIC: 'Diagnostic Labs',
  BULLION: 'Bullion / High-Value',
  BANK: 'Bank · CIT Phase 1',
  BANK_P2: 'Bank · CIT Phase 2',
  DEMO: 'Demo (All Channels)',
  CUSTOM: 'Custom Profile',
};

const PROFILE_TONES = {
  PHARMA: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  DIAGNOSTIC: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  BULLION: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  BANK: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  BANK_P2: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
  DEMO: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
  CUSTOM: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

export default function OpsDashboard() {
  const { tenant } = useTrackOticAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastPoll, setLastPoll] = useState(null);
  // KM report audit window — empty = "rolling 24h", both set = fixed audit window
  const [reportFromDate, setReportFromDate] = useState('');
  const [reportToDate, setReportToDate] = useState('');

  const buildReportUrl = (format) => {
    const params = new URLSearchParams();
    if (reportFromDate) params.set('from_date', reportFromDate);
    if (reportToDate) params.set('to_date', reportToDate);
    if (!reportFromDate && !reportToDate) params.set('hours', '24');
    return `${API_BASE}/api/v1/ops/reports/km.${format}?${params.toString()}`;
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/v1/ops/telemetry/dashboard?hours=24`);
      setData(res.data);
      setLastPoll(new Date());
    } catch (err) {
      // soft-fail: keep showing last good state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const id = setInterval(fetchDashboard, 5000);
    return () => clearInterval(id);
  }, [fetchDashboard]);

  if (loading && !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-fuchsia-500" />
      </div>
    );
  }
  if (!data) return null;

  const channels = data.channels || {};
  const kpis = data.kpis || {};
  const profile = data.tenant?.profile_type || 'CUSTOM';

  return (
    <div className="p-4 sm:p-6 space-y-6 min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950" data-testid="ops-dashboard">
      <SetPasswordBanner />
      {/* ===== Header ===== */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {data.tenant?.name || tenant?.name || 'Live Telemetry'}
            </h1>
            <Badge
              variant="outline"
              className={`uppercase tracking-wider text-[10px] font-mono ${PROFILE_TONES[profile]}`}
              data-testid="profile-badge"
            >
              {PROFILE_LABELS[profile] || profile}
            </Badge>
            <LivePulse lastPoll={lastPoll} />
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {profile === 'BANK' || profile === 'BANK_P2' ? 'Real-time GPS & CIT telemetry' : 'Real-time cold-chain telemetry'} • {kpis.total_readings?.toLocaleString() || 0} readings in last 24 h
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchDashboard}
          className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 self-start"
          data-testid="refresh-dashboard"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh now
        </Button>
      </div>

      {/* ===== Phase 36a — CIT metrics strip (BANK_P2 only) ===== */}
      {profile === 'BANK_P2' && <CitDashboardStrip />}

      {/* ===== KPI strip — channel-aware ===== */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-strip">
        <Kpi
          icon={Wifi} tone="emerald" label="Devices Online"
          value={`${kpis.devices_online}/${kpis.devices_total}`}
          sub="last 60 min"
          to="/ops/devices"
        />
        {channels.temperature && (
          <Kpi
            icon={Thermometer} tone="blue" label="Temperature"
            value={kpis.latest_temperature != null ? `${kpis.latest_temperature.toFixed(1)}°C` : '—'}
            sub="latest reading"
            to="/ops/cold-chain"
          />
        )}
        {channels.humidity && (
          <Kpi
            icon={Droplets} tone="cyan" label="Humidity"
            value={kpis.latest_humidity != null ? `${kpis.latest_humidity.toFixed(0)}%` : '—'}
            sub="latest reading"
            to="/ops/cold-chain"
          />
        )}
        {channels.weight && (
          <Kpi
            icon={Scale} tone="amber" label="Latest Weight"
            value={kpis.latest_weight != null ? `${kpis.latest_weight.toFixed(2)} kg` : '—'}
            sub={`${kpis.weight_deltas_count} delta events`}
            to="/ops/reports"
          />
        )}
        {channels.bag_events && (
          <Kpi
            icon={PackageOpen} tone="rose" label="Bag Events"
            value={kpis.bag_events_today}
            sub={`${kpis.open_bags_now} open now`}
            to="/ops/alerts"
          />
        )}
        {channels.gps && (
          <Kpi
            icon={MapPin} tone="violet" label="GPS Updates"
            value={(data.gps_points?.length || 0).toLocaleString()}
            sub="last 24 h"
            to="/ops/tracking"
          />
        )}
        {channels.gps && kpis.total_distance_km != null && (
          <Kpi
            icon={Route} tone="violet" label="KM Today"
            value={`${kpis.total_distance_km.toLocaleString()} km`}
            sub="all vehicles"
            to="/ops/tracking"
          />
        )}
        {channels.gps && kpis.halts_count != null && (
          <Kpi
            icon={Clock} tone="amber" label="Halts"
            value={kpis.halts_count}
            sub="≥ 5 min stops"
            to="/ops/tracking"
          />
        )}
        {kpis.telemetry_total_rows != null && (
          <Kpi
            icon={Database} tone="emerald" label="Telemetry Rows"
            value={kpis.telemetry_total_rows.toLocaleString()}
            sub="all time, this tenant"
            to="/ops/telemetry"
          />
        )}
      </div>

      {/* ===== Main grid ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Temperature chart */}
        {channels.temperature && (
          <ChartCard
            className="lg:col-span-2"
            icon={Thermometer}
            title="Temperature (°C) — 24 h"
            iconTone="text-blue-400"
            testid="temp-chart"
          >
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={(data.temperature_series || []).slice(-300)}>
                <defs>
                  <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#60a5fa" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="t" tick={false} stroke="#475569" />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" domain={['auto', 'auto']} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                  labelStyle={{ color: '#cbd5e1' }} itemStyle={{ color: '#60a5fa' }}
                  formatter={(v) => [`${v}°C`, 'Temp']}
                  labelFormatter={(t) => new Date(t).toLocaleTimeString()}
                />
                <ReferenceLine y={8} stroke="#f43f5e" strokeDasharray="3 3" label={{ value: 'Max 8°C', fill: '#fda4af', position: 'right', fontSize: 10 }} />
                <ReferenceLine y={2} stroke="#0ea5e9" strokeDasharray="3 3" label={{ value: 'Min 2°C', fill: '#7dd3fc', position: 'right', fontSize: 10 }} />
                <Area dataKey="v" stroke="#60a5fa" strokeWidth={2} fill="url(#tempGrad)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Humidity chart */}
        {channels.humidity && (
          <ChartCard
            icon={Droplets}
            title="Humidity (%) — 24 h"
            iconTone="text-cyan-400"
            testid="humidity-chart"
          >
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={(data.humidity_series || []).slice(-300)}>
                <XAxis dataKey="t" tick={false} stroke="#475569" />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" domain={[30, 80]} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8 }}
                  formatter={(v) => [`${v}%`, 'Humidity']}
                  labelFormatter={(t) => new Date(t).toLocaleTimeString()}
                />
                <Line dataKey="v" stroke="#22d3ee" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* GPS live map */}
        {channels.gps && (
          <ChartCard
            className="lg:col-span-2"
            icon={MapPin}
            title="Live GPS"
            iconTone="text-violet-400"
            extra={<Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">{data.live_devices?.length || 0} devices</Badge>}
            testid="gps-map"
          >
            <div className="h-80 rounded-lg overflow-hidden">
              <MapContainer center={[28.56, 77.22]} zoom={11} style={{ height: '100%', width: '100%' }} className="z-0">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap, &copy; CARTO"
                />
                {/* Route trail per device — last 80 points */}
                {Object.entries(groupGps(data.gps_points || [])).map(([dev, pts]) => (
                  <Polyline key={dev} positions={pts.slice(-80).map((p) => [p.lat, p.lng])} pathOptions={{ color: '#a78bfa', weight: 2, opacity: 0.6 }} />
                ))}
                {/* Live markers — latest position per device */}
                {(data.live_devices || []).filter((d) => d.current_lat && d.current_lng).map((d) => (
                  <Marker key={d.id} position={[d.current_lat, d.current_lng]} icon={liveIcon}>
                    <Popup>
                      <div className="text-xs">
                        <p className="font-bold">{d.device_uid}</p>
                        {d.current_temp != null && <p>Temp: {d.current_temp}°C</p>}
                        {d.current_humidity != null && <p>Humidity: {d.current_humidity}%</p>}
                        {d.current_weight != null && <p>Weight: {d.current_weight} kg</p>}
                        <p>Battery: {d.battery_level}%</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </ChartCard>
        )}

        {/* Bag events timeline */}
        {channels.bag_events && (
          <ChartCard
            icon={PackageOpen}
            title="Bag Open / Close Events"
            iconTone="text-rose-400"
            testid="bag-events"
          >
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {(data.bag_events || []).slice(-30).reverse().map((ev) => (
                <div
                  key={`${ev.t}-${ev.device}-${ev.event}`}
                  className="flex items-center justify-between text-xs p-2 rounded-md bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full ${ev.event === 'bag_open' ? 'bg-rose-400' : 'bg-emerald-400'}`} />
                    <span className="font-mono text-slate-700 dark:text-slate-300 truncate">{ev.device}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={ev.event === 'bag_open' ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}>
                      {ev.event === 'bag_open' ? 'OPEN' : 'CLOSE'}
                    </span>
                    <span className="text-slate-500">{new Date(ev.t).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))}
              {(!data.bag_events || data.bag_events.length === 0) && (
                <div className="text-center text-slate-500 text-sm py-8">No bag events in window</div>
              )}
            </div>
          </ChartCard>
        )}

        {/* Weight delta panel */}
        {channels.weight && (
          <ChartCard
            className="lg:col-span-2"
            icon={Scale}
            title="Weight Changes — delivery deltas"
            iconTone="text-amber-400"
            testid="weight-deltas"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1">
              {(data.weight_deltas || []).slice(-12).reverse().map((wd) => (
                <div
                  key={`${wd.t}-${wd.device}`}
                  className="p-3 rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-600 dark:text-slate-400">{wd.device}</span>
                    <Badge className={wd.delta < 0 ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' : 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30'}>
                      {wd.delta > 0 ? '+' : ''}{wd.delta} kg
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-baseline gap-2 text-sm">
                    <span className="text-slate-500 line-through">{wd.before} kg</span>
                    <ChevronRight className="w-3 h-3 text-slate-600" />
                    <span className="text-slate-900 dark:text-white font-semibold">{wd.after} kg</span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">{new Date(wd.t).toLocaleString()}</p>
                </div>
              ))}
              {(!data.weight_deltas || data.weight_deltas.length === 0) && (
                <div className="col-span-2 text-center text-slate-500 text-sm py-8">No weight changes recorded</div>
              )}
            </div>
          </ChartCard>
        )}

        {/* Per-device KM verification (Bank CIT Phase 1 headline) */}
        {channels.gps && data.distance_km_per_device && Object.keys(data.distance_km_per_device).length > 0 && (
          <ChartCard
            className="lg:col-span-2"
            icon={Route}
            title="KM Verification — per device (24 h)"
            iconTone="text-violet-400"
            testid="km-verification"
            extra={
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <input
                  type="date"
                  value={reportFromDate}
                  onChange={(e) => setReportFromDate(e.target.value)}
                  data-testid="report-from-date"
                  className="text-xs bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded px-2 py-1"
                  aria-label="From date"
                />
                <span className="text-slate-500 text-xs">→</span>
                <input
                  type="date"
                  value={reportToDate}
                  onChange={(e) => setReportToDate(e.target.value)}
                  data-testid="report-to-date"
                  className="text-xs bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded px-2 py-1"
                  aria-label="To date"
                />
                <a
                  href={buildReportUrl('xlsx')}
                  download
                  data-testid="download-km-xlsx"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25"
                >
                  <FileSpreadsheet className="w-3 h-3" /> Excel
                </a>
                <a
                  href={buildReportUrl('csv')}
                  download
                  data-testid="download-km-csv"
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-slate-200 dark:bg-slate-700/60 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-slate-300 dark:hover:bg-slate-700"
                >
                  <Download className="w-3 h-3" /> CSV
                </a>
              </div>
            }
          >
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {Object.entries(data.distance_km_per_device)
                .sort((a, b) => b[1] - a[1])
                .map(([dev, km]) => (
                <div key={dev} className="flex items-center justify-between p-2 rounded-md bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/50">
                  <span className="font-mono text-xs text-slate-700 dark:text-slate-300">{dev}</span>
                  <span className="text-sm font-semibold text-violet-700 dark:text-violet-300">{km.toFixed(2)} km</span>
                </div>
              ))}
            </div>
          </ChartCard>
        )}

        {/* Halts timeline (Phase 1 — halt history) */}
        {channels.gps && data.halts && (
          <ChartCard
            className="lg:col-span-2"
            icon={Clock}
            title="Trip Halts — last 24 h"
            iconTone="text-amber-400"
            testid="halts-timeline"
          >
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {data.halts.slice(0, 40).map((h, i) => (
                <div
                  key={`${h.device}-${h.start}-${i}`}
                  className="flex items-center justify-between text-xs p-2 rounded-md bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className={`w-3 h-3 shrink-0 ${h.ongoing ? 'text-emerald-500' : 'text-amber-500'}`} />
                    <span className="font-mono text-slate-700 dark:text-slate-300 truncate">{h.device}</span>
                    {h.ongoing && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">LIVE</span>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-slate-500 dark:text-slate-400 font-mono text-[10px]">
                      {parseFloat(h.lat).toFixed(4)}, {parseFloat(h.lng).toFixed(4)}
                    </span>
                    <span className="text-amber-600 dark:text-amber-300 font-semibold">{h.duration_min} min</span>
                    <span className="text-slate-500 font-mono text-[10px]">
                      {new Date(h.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))}
              {data.halts.length === 0 && (
                <div className="text-center text-slate-500 text-sm py-8">No halts in window (vehicles never stopped &ge; 5 min)</div>
              )}
            </div>
          </ChartCard>
        )}

        {/* Live devices table */}
        <ChartCard
          className="lg:col-span-3"
          icon={Activity}
          title="Live Device Status"
          iconTone="text-emerald-400"
          testid="live-devices"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 text-xs uppercase tracking-wider">
                  <th className="py-2 pr-3">Device</th>
                  <th className="py-2 px-3">Status</th>
                  {channels.temperature && <th className="py-2 px-3">Temp</th>}
                  {channels.humidity && <th className="py-2 px-3">Humidity</th>}
                  {channels.weight && <th className="py-2 px-3">Weight</th>}
                  <th className="py-2 px-3">Battery</th>
                  <th className="py-2 px-3">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {(data.live_devices || []).map((d, idx) => (
                  <tr key={d.id || d.device_uid || `dev-${idx}`} className="border-b border-slate-200 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                    <td className="py-2 pr-3 font-mono text-slate-800 dark:text-slate-300">{d.device_uid}</td>
                    <td className="py-2 px-3">
                      <Badge className={d.status === 'active' ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'}>
                        {d.status}
                      </Badge>
                    </td>
                    {channels.temperature && (
                      <td className="py-2 px-3 text-blue-600 dark:text-blue-300">{d.current_temp != null ? `${d.current_temp}°C` : '—'}</td>
                    )}
                    {channels.humidity && (
                      <td className="py-2 px-3 text-cyan-600 dark:text-cyan-300">{d.current_humidity != null ? `${d.current_humidity}%` : '—'}</td>
                    )}
                    {channels.weight && (
                      <td className="py-2 px-3 text-amber-600 dark:text-amber-300">{d.current_weight != null ? `${d.current_weight} kg` : '—'}</td>
                    )}
                    <td className="py-2 px-3 text-slate-700 dark:text-slate-300">{d.battery_level != null ? `${d.battery_level}%` : '—'}</td>
                    <td className="py-2 px-3 text-slate-500 text-xs">
                      {d.last_seen_at ? new Date(d.last_seen_at).toLocaleTimeString() : 'never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

/* ---------------- small subcomponents ---------------- */

function LivePulse({ lastPoll }) {
  const fresh = lastPoll && (Date.now() - lastPoll.getTime() < 15000);
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={`relative inline-flex w-2 h-2 rounded-full ${fresh ? 'bg-emerald-500' : 'bg-amber-500'}`}>
        {fresh && <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" />}
      </span>
      <span className={fresh ? 'text-emerald-300' : 'text-amber-300'}>
        {fresh ? 'LIVE' : 'reconnecting'}
      </span>
    </span>
  );
}

function Kpi({ icon: Icon, tone, label, value, sub, to }) {
  const tones = {
    emerald: 'border-emerald-500/30 from-emerald-500/10 to-transparent text-emerald-700 dark:text-emerald-300',
    blue: 'border-blue-500/30 from-blue-500/10 to-transparent text-blue-700 dark:text-blue-300',
    cyan: 'border-cyan-500/30 from-cyan-500/10 to-transparent text-cyan-700 dark:text-cyan-300',
    amber: 'border-amber-500/30 from-amber-500/10 to-transparent text-amber-700 dark:text-amber-300',
    rose: 'border-rose-500/30 from-rose-500/10 to-transparent text-rose-700 dark:text-rose-300',
    violet: 'border-violet-500/30 from-violet-500/10 to-transparent text-violet-700 dark:text-violet-300',
  };
  const interactive = to
    ? 'cursor-pointer transition hover:shadow-md hover:-translate-y-0.5 hover:border-opacity-60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent focus:ring-slate-400'
    : '';
  const body = (
    <>
      <div className="flex items-center justify-between">
        <Icon className="w-4 h-4 opacity-80" />
        {to && <ChevronRight className="w-3 h-3 opacity-40" />}
      </div>
      <p className="text-xl font-bold text-slate-900 dark:text-white mt-2">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 mt-0.5">{label}</p>
      {sub && <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-0.5">{sub}</p>}
    </>
  );
  const className = `block rounded-xl border bg-white dark:bg-transparent bg-gradient-to-br ${tones[tone]} backdrop-blur p-3 ${interactive}`;
  const testId = `kpi-${label.toLowerCase().replace(/\s+/g, '-')}`;
  if (to) {
    return (
      <Link to={to} className={className} data-testid={testId}>
        {body}
      </Link>
    );
  }
  return (
    <div className={className} data-testid={testId}>
      {body}
    </div>
  );
}

function ChartCard({ icon: Icon, iconTone, title, children, extra, className = '', testid }) {
  return (
    <div className={`bg-white dark:bg-slate-900/60 backdrop-blur rounded-xl border border-slate-200 dark:border-slate-800 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${iconTone}`} />}
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h2>
        </div>
        {extra}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function groupGps(points) {
  const acc = {};
  for (const p of points) {
    if (!acc[p.device]) acc[p.device] = [];
    acc[p.device].push(p);
  }
  return acc;
}

// Phase 36a — CIT metrics strip + Vans Now + Open Alerts widgets (BANK_P2 tenants)
function CitDashboardStrip() {
  const [cit, setCit] = useState(null);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await opsApi.getCitDashboard();
        if (active) setCit(res.data);
      } catch { /* non-fatal — strip just stays hidden */ }
    };
    load();
    const id = setInterval(load, 10000);
    return () => { active = false; clearInterval(id); };
  }, []);

  if (!cit) return null;
  const m = cit.metrics || {};
  return (
    <div className="space-y-4" data-testid="cit-dashboard-strip">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <CitMetricCard icon={Truck}    tone="emerald" label="Vans on duty"    value={`${m.vans_on_duty?.current ?? 0}/${m.vans_on_duty?.total ?? 0}`} to="/ops/vans" />
        <CitMetricCard icon={Wifi}     tone="blue"    label="Devices online"  value={`${m.devices_online?.current ?? 0}/${m.devices_online?.total ?? 0}`} to="/ops/vans" />
        <CitMetricCard icon={Route}    tone="violet"  label="KM today (GPS)"  value={(m.km_today?.gps ?? 0).toFixed(1)} />
        <CitMetricCard icon={MapPin}   tone="amber"   label="Stops"           value={`${m.stops?.completed ?? 0}/${m.stops?.planned ?? 0}`} sub="completed / planned" />
        <CitMetricCard icon={Bell}     tone="rose"    label="Open alerts"     value={m.open_alerts ?? 0} to="/ops/alerts" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard icon={Truck} iconTone="text-emerald-400" title="Vans now" testid="vans-now-widget"
          extra={<Link to="/ops/vans" className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1">All vans <ChevronRight className="w-3 h-3" /></Link>}>
          {(cit.vans_now || []).length === 0 ? (
            <p className="text-slate-500 text-sm">No vans currently reporting.</p>
          ) : (
            <div className="space-y-2">
              {cit.vans_now.slice(0, 6).map((v, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="text-slate-900 dark:text-white font-medium">{v.van_id}</p>
                    <p className="text-slate-500 text-xs">{v.driver_name || 'No driver assigned'}</p>
                  </div>
                  <Badge variant="outline" className={vansNowTone(v.status)}>{vansNowLabel(v.status)}</Badge>
                </div>
              ))}
            </div>
          )}
        </ChartCard>

        <ChartCard icon={Bell} iconTone="text-rose-400" title="Open alerts" testid="open-alerts-widget"
          extra={<Link to="/ops/alerts" className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1">All alerts <ChevronRight className="w-3 h-3" /></Link>}>
          {(cit.open_alerts || []).length === 0 ? (
            <p className="text-slate-500 text-sm">No open alerts.</p>
          ) : (
            <div className="space-y-2">
              {cit.open_alerts.slice(0, 6).map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="text-slate-900 dark:text-white font-medium truncate">{a.type}</p>
                    <p className="text-slate-500 text-xs truncate">{a.message}</p>
                  </div>
                  <Badge variant="outline" className={alertLevelTone(a.level)}>{a.level}</Badge>
                </div>
              ))}
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

function CitMetricCard({ icon: Icon, tone, label, value, sub, to }) {
  const tones = {
    emerald: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20 text-emerald-400',
    blue:    'from-blue-500/10 to-blue-500/5 border-blue-500/20 text-blue-400',
    violet:  'from-violet-500/10 to-violet-500/5 border-violet-500/20 text-violet-400',
    amber:   'from-amber-500/10 to-amber-500/5 border-amber-500/20 text-amber-400',
    rose:    'from-rose-500/10 to-rose-500/5 border-rose-500/20 text-rose-400',
  };
  const body = (
    <>
      <div className="flex items-center justify-between">
        <p className="text-slate-500 text-xs uppercase tracking-wider">{label}</p>
        <Icon className={`w-4 h-4 ${tones[tone].split(' ').pop()}`} />
      </div>
      <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1 font-mono">{value}</p>
      {sub && <p className="text-slate-500 text-xs mt-0.5">{sub}</p>}
    </>
  );
  const cls = `block rounded-xl border bg-white dark:bg-transparent bg-gradient-to-br ${tones[tone]} backdrop-blur p-3 ${to ? 'hover:scale-[1.02] transition-transform' : ''}`;
  return to ? <Link to={to} className={cls}>{body}</Link> : <div className={cls}>{body}</div>;
}

function vansNowTone(status) {
  switch (status) {
    case 'moving':     return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
    case 'idle':       return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'offline':    return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'no_device':  return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    default:           return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}
function vansNowLabel(status) {
  switch (status) {
    case 'moving':    return 'Moving';
    case 'idle':      return 'Idle';
    case 'offline':   return 'Offline';
    case 'no_device': return 'No device';
    default:          return status || '—';
  }
}
function alertLevelTone(level) {
  switch (level) {
    case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'warning':  return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'info':     return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    default:         return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  }
}
